import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "react-feather";
import Chart from "react-apexcharts";
import { toast } from "react-toastify";
import { getBillingDashboardAPI } from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./BillingDashboard.module.scss";

// ─── Chart colours keyed to #644998 ──────────────────────────────────────────
const CHART_COLOR = "#644998";

function overdueSeverity(days) {
  if (days > 60) return "danger";
  if (days > 30) return "warn";
  return "ok";
}

// ─── Loading skeleton ─────────────────────────────────────────────────────────
function LoadingSkeleton() {
  return (
    <div className={styles.body}>
      <div className={styles.skeletonKpiRow}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className={styles.skeletonKpi} />
        ))}
      </div>
      <div className={styles.skeletonCharts}>
        <div className={styles.skeletonChart} />
        <div className={styles.skeletonChart} />
      </div>
      <div className={styles.skeletonTable} />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function BillingDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBillingDashboardAPI();
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ─── Chart options ───────────────────────────────────────────────────────
  const lineOpts = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: "DM Sans, Segoe UI, sans-serif",
      },
      colors: [CHART_COLOR],
      xaxis: {
        categories: (data?.revenueTrend12m || []).map((x) => x.month),
        labels: { style: { colors: "#a897c1" } },
      },
      yaxis: {
        labels: {
          style: { colors: "#a897c1" },
          formatter: (v) => `$${(v / 1000).toFixed(0)}k`,
        },
      },
      stroke: { curve: "smooth", width: 3 },
      dataLabels: { enabled: false },
      grid: { borderColor: "rgba(100,73,152,0.10)" },
      tooltip: { theme: "light" },
    }),
    [data],
  );

  const barOpts = useMemo(
    () => ({
      chart: {
        toolbar: { show: false },
        fontFamily: "DM Sans, Segoe UI, sans-serif",
      },
      colors: [CHART_COLOR],
      xaxis: {
        categories: (data?.invoiceAging || []).map((x) => x.bucket),
        labels: { style: { colors: "#a897c1" } },
      },
      yaxis: {
        labels: {
          style: { colors: "#a897c1" },
          formatter: (v) => `$${(v / 1000).toFixed(0)}k`,
        },
      },
      plotOptions: { bar: { columnWidth: "50%", borderRadius: 6 } },
      dataLabels: { enabled: false },
      grid: { borderColor: "rgba(100,73,152,0.10)" },
      tooltip: { theme: "light" },
    }),
    [data],
  );

  // ─── CSV export ──────────────────────────────────────────────────────────
  const exportReport = () => {
    const lines = [
      "Company,Plan,Invoice,Amount,DaysOverdue",
      ...(data?.overdueTenants || []).map((t) =>
        [
          t.companyName,
          t.plan,
          t.invoiceNumber,
          t.amountUsd,
          t.daysOverdue,
        ].join(","),
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "billing-overdue.csv";
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success("Report exported");
  };

  // ─── Shared header props ─────────────────────────────────────────────────
  const headerProps = {
    title: "Billing dashboard",
    breadcrumbs: [
      { label: "Platform", path: platformUrl("dashboard") },
      { label: "Billing" },
    ],
    primaryAction: { label: "Export report", onClick: exportReport },
    secondaryActions: [
      {
        label: "Invoices",
        onClick: () => navigate(platformUrl("billing/invoices")),
      },
      {
        label: "Payment issues",
        onClick: () => navigate(platformUrl("billing/payment-issues")),
      },
    ],
  };

  if (loading && !data) {
    return (
      <div className={styles.page}>
        <PageHeader {...headerProps} />
        <LoadingSkeleton />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className={styles.page}>
        <PageHeader {...headerProps} />
        <div className={styles.body}>
          <div className={styles.emptyState}>
            <AlertTriangle size={40} />
            <h3>Could not load billing data</h3>
            <p>{error.message}</p>
            <button className={styles.btnPrimary} onClick={load}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader {...headerProps} />

      <div className={styles.body}>
        {/* ── KPI cards ─────────────────────────────────────────────────── */}
        <div className={styles.kpiRow}>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>MRR</div>
            <div className={styles.kpiValue}>
              ${(data.mrrUsd / 1000).toFixed(0)}k
            </div>
            <div className={styles.kpiSub}>Monthly recurring revenue</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>ARR</div>
            <div className={styles.kpiValue}>
              ${(data.arrUsd / 1000000).toFixed(2)}M
            </div>
            <div className={styles.kpiSub}>Annual recurring revenue</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>Outstanding</div>
            <div className={styles.kpiValue}>
              ${data.outstandingUsd.toLocaleString()}
            </div>
            <div className={styles.kpiSub}>Unpaid invoices</div>
          </div>
          <div className={styles.kpiCard}>
            <div className={styles.kpiLabel}>Failed payments</div>
            <div className={styles.kpiValue}>{data.failedPayments}</div>
            <div className={styles.kpiSub}>Requires attention</div>
          </div>
        </div>

        {/* ── Charts ────────────────────────────────────────────────────── */}
        <div className={styles.chartsRow}>
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Revenue trend</h2>
              <span className={styles.cardBadge}>12 months</span>
            </div>
            <div className={styles.cardBody}>
              <Chart
                options={lineOpts}
                series={[
                  {
                    name: "Revenue",
                    data: (data.revenueTrend12m || []).map((x) => x.value),
                  },
                ]}
                type="line"
                height={300}
              />
            </div>
          </div>

          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <h2>Invoice aging</h2>
              <span className={styles.cardBadge}>USD</span>
            </div>
            <div className={styles.cardBody}>
              <Chart
                options={barOpts}
                series={[
                  {
                    name: "USD",
                    data: (data.invoiceAging || []).map((x) => x.amountUsd),
                  },
                ]}
                type="bar"
                height={300}
              />
            </div>
          </div>
        </div>

        {/* ── Overdue tenants table ──────────────────────────────────────── */}
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <h2>Overdue tenants</h2>
            <span className={styles.cardBadge}>
              {(data.overdueTenants || []).length} tenants
            </span>
          </div>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Plan</th>
                  <th>Invoice #</th>
                  <th>Amount</th>
                  <th>Due date</th>
                  <th>Days overdue</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {(data.overdueTenants || []).map((t) => (
                  <tr
                    key={t.id}
                    className={t.daysOverdue > 60 ? styles.overdue : ""}
                  >
                    <td>{t.companyName}</td>
                    <td>{t.plan}</td>
                    <td>
                      <span className={styles.invoiceNum}>
                        {t.invoiceNumber}
                      </span>
                    </td>
                    <td>${t.amountUsd.toLocaleString()}</td>
                    <td>{new Date(t.dueDate).toLocaleDateString()}</td>
                    <td>
                      <span
                        className={`${styles.daysOverdueBadge} ${styles[overdueSeverity(t.daysOverdue)]}`}
                      >
                        {t.daysOverdue}d
                      </span>
                    </td>
                    <td>
                      <div className={styles.tdActions}>
                        <button
                          className={`${styles.btnOutline} ${styles.btnSm}`}
                          onClick={() => toast.success("Reminder sent")}
                        >
                          Send reminder
                        </button>
                        <button
                          className={`${styles.btnGhost} ${styles.btnSm}`}
                          onClick={() =>
                            navigate(platformUrl("billing/invoices"))
                          }
                        >
                          Open invoice
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
